import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';

const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: '/main-dashboard',
    icon: 'LayoutDashboard',
    tooltip: 'Panel de control principal',
  },
  {
    label: 'Calculadora',
    path: '/batch-calculator',
    icon: 'Calculator',
    tooltip: 'Cálculo de precios por lote',
  },
  {
    label: 'Lotes',
    path: '/active-batch-management',
    icon: 'Layers',
    tooltip: 'Gestión de lotes activos',
  },
  {
    label: 'Inventario',
    path: '/inventory',
    icon: 'Package',
    tooltip: 'Control de inventario actual',
  },
  {
    label: 'Venta de Stock',
    path: '/stock-sales',
    icon: 'TrendingUp',
    tooltip: 'Ventas contra stock agregado por categoría',
  },
  {
    label: 'Análisis de Ventas',
    path: '/sales-analysis',
    icon: 'BarChart2',
    tooltip: 'Análisis de ventas y rendimiento',
  },
  {
    label: 'Alertas',
    path: '/alerts-dashboard',
    icon: 'Bell',
    tooltip: 'Panel de alertas en tiempo real',
  },
  {
    label: 'Mercado',
    path: '/market-data',
    icon: 'TrendingUp',
    tooltip: 'Datos de mercado en tiempo real',
  },
  {
    label: 'Configuración',
    path: '/settings',
    icon: 'Settings',
    tooltip: 'Parámetros del sistema',
  },
];

const USER_MENU_ITEMS = [
  { label: 'Configuración', path: '/settings', icon: 'Settings' },
  { label: 'Preferencias', path: '/preferences', icon: 'SlidersHorizontal' },
  { label: 'Ayuda', path: '/help', icon: 'HelpCircle' },
];

export default function PrimaryNavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);

  // Simulated real-time state
  const [marketStatus, setMarketStatus] = useState('connected'); // 'connected' | 'disconnected' | 'updating'
  const [lastUpdate, setLastUpdate] = useState('16:35');
  const [alertCount, setAlertCount] = useState(3);
  const [alerts, setAlerts] = useState([
    { id: 1, type: 'warning', message: 'Inventario de cobre bajo umbral mínimo', time: '16:30', path: '/inventory' },
    { id: 2, type: 'error', message: 'Precio LME cayó 2.3% en última hora', time: '16:22', path: '/market-data' },
    { id: 3, type: 'success', message: 'Lote #2847 procesado exitosamente', time: '16:10', path: '/active-batch-management' },
  ]);

  const moreRef = useRef(null);
  const userRef = useRef(null);
  const alertsRef = useRef(null);
  const mobileRef = useRef(null);

  const isActive = (path) => location?.pathname === path;

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef?.current && !moreRef?.current?.contains(e?.target)) setMoreOpen(false);
      if (userRef?.current && !userRef?.current?.contains(e?.target)) setUserOpen(false);
      if (alertsRef?.current && !alertsRef?.current?.contains(e?.target)) setAlertsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMoreOpen(false);
    setUserOpen(false);
    setAlertsOpen(false);
  }, [location?.pathname]);

  // Simulate market status updates
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setLastUpdate(`${String(now?.getHours())?.padStart(2, '0')}:${String(now?.getMinutes())?.padStart(2, '0')}`);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDismissAlert = (e, id) => {
    e?.preventDefault();
    e?.stopPropagation();
    setAlerts((prev) => prev?.filter((a) => a?.id !== id));
    setAlertCount((prev) => Math.max(0, prev - 1));
  };

  const handleSignOut = async () => {
    setUserOpen(false);
    setMobileOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  const alertTypeIcon = (type) => {
    if (type === 'warning') return { icon: 'AlertTriangle', color: 'text-warning' };
    if (type === 'error') return { icon: 'AlertCircle', color: 'text-error' };
    return { icon: 'CheckCircle', color: 'text-success' };
  };

  const marketStatusConfig = {
    connected: { color: 'bg-success', label: 'LME Conectado', pulse: true },
    disconnected: { color: 'bg-error', label: 'LME Desconectado', pulse: false },
    updating: { color: 'bg-warning', label: 'Actualizando...', pulse: true },
  };

  const statusCfg = marketStatusConfig?.[marketStatus];

  return (
    <>
      {/* Primary Navigation Bar */}
      <header
        className="fixed top-0 left-0 right-0 z-[100] bg-card border-b border-border"
        style={{ height: '60px', boxShadow: 'var(--shadow-md)' }}
      >
        <div className="flex items-center h-full px-4 lg:px-6 gap-4">

          {/* Logo - embedded directly */}
          <Link
            to="/main-dashboard"
            className="flex items-center gap-2.5 flex-shrink-0 group"
            aria-label="MetalRecovery - Inicio"
          >
            <div
              className="flex items-center justify-center rounded-md transition-standard group-hover:scale-105"
              style={{
                width: '36px',
                height: '36px',
                background: 'var(--color-accent)',
                borderRadius: '8px',
              }}
            >
              <Icon name="Zap" size={20} color="#FFFFFF" strokeWidth={2.5} />
            </div>
            <span
              className="font-heading font-700 text-foreground hidden sm:block"
              style={{ fontSize: '1.125rem', fontWeight: '700', letterSpacing: '-0.01em' }}
            >
              Metal<span style={{ color: 'var(--color-accent)' }}>Recovery</span>
            </span>
          </Link>

          {/* Desktop Navigation Items */}
          <nav className="hidden lg:flex items-center gap-1 flex-1 ml-4" aria-label="Navegación principal">
            {NAV_ITEMS?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                title={item?.tooltip}
                data-tour={item?.path === '/batch-calculator' ? 'nav-batch-calculator' : item?.path === '/inventory' ? 'nav-inventory' : item?.path === '/alerts-dashboard' ? 'nav-alerts' : undefined}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium
                  transition-standard hover:bg-muted relative
                  ${isActive(item?.path)
                    ? 'text-accent bg-muted' :'text-muted-foreground hover:text-foreground'
                  }
                `}
                style={{ fontFamily: 'var(--font-caption, "IBM Plex Sans", sans-serif)', fontSize: '0.875rem' }}
                aria-current={isActive(item?.path) ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={16} color="currentColor" strokeWidth={isActive(item?.path) ? 2.5 : 2} />
                {item?.label}
                {isActive(item?.path) && (
                  <span
                    className="absolute bottom-0 left-2 right-2 h-0.5 rounded-t"
                    style={{ background: 'var(--color-accent)' }}
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* Right Section: Market Status + Alerts + User */}
          <div className="flex items-center gap-2 ml-auto">

            {/* Market Data Status Indicator */}
            <div
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted border border-border"
              title={`${statusCfg?.label} - Última actualización: ${lastUpdate}`}
            >
              <div className="relative flex items-center">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${statusCfg?.color}`}
                />
                {statusCfg?.pulse && (
                  <span
                    className={`absolute inline-block w-2 h-2 rounded-full ${statusCfg?.color} animate-ping opacity-75`}
                  />
                )}
              </div>
              <span
                className="text-muted-foreground hidden lg:block"
                style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
              >
                LME {lastUpdate}
              </span>
            </div>

            {/* Mobile Market Status - icon only */}
            <div
              className="flex md:hidden items-center justify-center w-8 h-8"
              title={`${statusCfg?.label} - ${lastUpdate}`}
            >
              <div className="relative">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${statusCfg?.color}`} />
                {statusCfg?.pulse && (
                  <span className={`absolute inset-0 inline-block w-2.5 h-2.5 rounded-full ${statusCfg?.color} animate-ping opacity-75`} />
                )}
              </div>
            </div>

            {/* Risk Alert Notification */}
            <div className="relative" ref={alertsRef}>
              <button
                onClick={() => setAlertsOpen((v) => !v)}
                className="relative flex items-center justify-center w-10 h-10 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-standard focus-visible:outline-2 focus-visible:outline-ring"
                aria-label={`Alertas de riesgo - ${alertCount} activas`}
                aria-expanded={alertsOpen}
                aria-haspopup="true"
              >
                <Icon name="Bell" size={20} color="currentColor" strokeWidth={2} />
                {alertCount > 0 && (
                  <span
                    className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 rounded-full text-white spring-in"
                    style={{
                      background: 'var(--color-error)',
                      fontSize: '0.625rem',
                      fontFamily: '"IBM Plex Sans", sans-serif',
                      fontWeight: '600',
                      lineHeight: 1,
                    }}
                  >
                    {alertCount > 9 ? '9+' : alertCount}
                  </span>
                )}
              </button>

              {alertsOpen && (
                <div
                  className="absolute top-full right-0 mt-1 w-80 bg-card border border-border rounded-md overflow-hidden animate-slide-down"
                  style={{ boxShadow: 'var(--shadow-xl)', zIndex: 50 }}
                  role="dialog"
                  aria-label="Panel de alertas"
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <span className="font-heading font-semibold text-foreground" style={{ fontSize: '0.9375rem' }}>
                      Alertas de Riesgo
                    </span>
                    {alertCount > 0 && (
                      <span className="badge-error">{alertCount} activas</span>
                    )}
                  </div>

                  <div className="max-h-72 overflow-y-auto">
                    {alerts?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
                        <Icon name="CheckCircle" size={32} color="var(--color-success)" strokeWidth={1.5} />
                        <span style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>
                          Sin alertas activas
                        </span>
                      </div>
                    ) : (
                      alerts?.map((alert) => {
                        const { icon, color } = alertTypeIcon(alert?.type);
                        return (
                          <Link
                            key={alert?.id}
                            to={alert?.path}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-muted transition-standard border-b border-border last:border-0 group"
                            onClick={() => setAlertsOpen(false)}
                          >
                            <Icon name={icon} size={18} className={color} color="currentColor" strokeWidth={2} />
                            <div className="flex-1 min-w-0">
                              <p
                                className="text-foreground leading-snug"
                                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}
                              >
                                {alert?.message}
                              </p>
                              <span
                                className="text-muted-foreground"
                                style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', fontVariantNumeric: 'tabular-nums' }}
                              >
                                {alert?.time}
                              </span>
                            </div>
                            <button
                              onClick={(e) => handleDismissAlert(e, alert?.id)}
                              className="opacity-0 group-hover:opacity-100 transition-standard text-muted-foreground hover:text-foreground flex-shrink-0 mt-0.5"
                              aria-label="Descartar alerta"
                            >
                              <Icon name="X" size={14} color="currentColor" strokeWidth={2} />
                            </button>
                          </Link>
                        );
                      })
                    )}
                  </div>

                  {alerts?.length > 0 && (
                    <div className="px-4 py-2.5 border-t border-border">
                      <button
                        onClick={() => { setAlerts([]); setAlertCount(0); }}
                        className="text-accent hover:text-accent transition-standard w-full text-center"
                        style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', fontWeight: '500' }}
                      >
                        Descartar todas
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Context Menu */}
            <div className="relative" ref={userRef}>
              <button
                onClick={() => setUserOpen((v) => !v)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted transition-standard focus-visible:outline-2 focus-visible:outline-ring"
                aria-expanded={userOpen}
                aria-haspopup="true"
                aria-label="Menú de usuario"
              >
                <div
                  className="flex items-center justify-center w-8 h-8 rounded-full text-white flex-shrink-0"
                  style={{ background: 'var(--color-accent)', fontSize: '0.75rem', fontFamily: '"Outfit", sans-serif', fontWeight: '600' }}
                >
                  MR
                </div>
                <span
                  className="hidden lg:block text-foreground"
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}
                >
                  Operador
                </span>
                <Icon
                  name="ChevronDown"
                  size={14}
                  color="currentColor"
                  className={`hidden lg:block text-muted-foreground transition-standard ${userOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {userOpen && (
                <div
                  className="absolute top-full right-0 mt-1 w-52 bg-card border border-border rounded-md py-1 overflow-hidden animate-slide-down"
                  style={{ boxShadow: 'var(--shadow-xl)', zIndex: 50 }}
                  role="menu"
                >
                  <div className="px-4 py-3 border-b border-border">
                    <p
                      className="font-semibold text-foreground"
                      style={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9375rem' }}
                    >
                      Operador
                    </p>
                    <p
                      className="text-muted-foreground"
                      style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}
                    >
                      operador@metalrecovery.com
                    </p>
                  </div>

                  {USER_MENU_ITEMS?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`
                        flex items-center gap-3 px-4 py-2.5 text-sm transition-standard hover:bg-muted
                        ${isActive(item?.path) ? 'text-accent' : 'text-foreground'}
                      `}
                      style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}
                      role="menuitem"
                    >
                      <Icon name={item?.icon} size={16} color="currentColor" strokeWidth={2} />
                      {item?.label}
                    </Link>
                  ))}

                  <div className="border-t border-border mt-1 pt-1">
                    <button
                      className="flex items-center gap-3 px-4 py-2.5 w-full text-left text-destructive hover:bg-muted transition-standard"
                      style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}
                      role="menuitem"
                      onClick={handleSignOut}
                    >
                      <Icon name="LogOut" size={16} color="currentColor" strokeWidth={2} />
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-standard"
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={mobileOpen}
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={22} color="currentColor" strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[99] bg-background"
          style={{ top: '60px' }}
          ref={mobileRef}
          role="dialog"
          aria-label="Menú de navegación móvil"
        >
          <nav className="flex flex-col p-4 gap-1 overflow-y-auto h-full pb-24">
            {/* Mobile Market Status */}
            <div
              className="flex items-center gap-3 px-4 py-3 rounded-md bg-muted mb-2"
            >
              <div className="relative">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${statusCfg?.color}`} />
                {statusCfg?.pulse && (
                  <span className={`absolute inset-0 inline-block w-2.5 h-2.5 rounded-full ${statusCfg?.color} animate-ping opacity-75`} />
                )}
              </div>
              <span
                className="text-muted-foreground"
                style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
              >
                {statusCfg?.label} — {lastUpdate}
              </span>
            </div>

            {/* All Nav Items on Mobile */}
            {[...NAV_ITEMS]?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-md transition-standard min-h-[48px]
                  ${isActive(item?.path)
                    ? 'bg-muted text-accent' :'text-foreground hover:bg-muted'
                  }
                `}
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem', fontWeight: '500' }}
                aria-current={isActive(item?.path) ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={20} color="currentColor" strokeWidth={isActive(item?.path) ? 2.5 : 2} />
                {item?.label}
                {isActive(item?.path) && (
                  <Icon name="ChevronRight" size={16} color="currentColor" className="ml-auto text-accent" strokeWidth={2.5} />
                )}
              </Link>
            ))}

            <div className="border-t border-border my-2" />

            {/* User items on mobile */}
            {USER_MENU_ITEMS?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-md transition-standard min-h-[48px]
                  ${isActive(item?.path) ? 'text-accent bg-muted' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                `}
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
              >
                <Icon name={item?.icon} size={20} color="currentColor" strokeWidth={2} />
                {item?.label}
              </Link>
            ))}

            <button
              className="flex items-center gap-3 px-4 py-3 rounded-md transition-standard min-h-[48px] text-destructive hover:bg-muted w-full text-left mt-auto"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
              onClick={handleSignOut}
            >
              <Icon name="LogOut" size={20} color="currentColor" strokeWidth={2} />
              Cerrar sesión
            </button>
          </nav>
        </div>
      )}
      {/* Spacer to prevent content from hiding behind fixed nav */}
      <div style={{ height: '60px' }} aria-hidden="true" />
    </>
  );
}